import {ALL_SITE_LOCALES, type SiteLocale} from "seo/siteMeta";

/**
 * Completes a per-locale map by answering English wherever a locale has nothing of its own.
 *
 * A storefront ships before its copy is translated. The German market (kitecorp/transferify-ui#1498)
 * launched with an English catalogue behind it and locawise fills it in kitecorp/transferify-ui#1499,
 * so every `Record<SiteLocale, T>` in the app has to have an answer for a locale nobody has written
 * yet. This is that answer, in ONE place: the alternative — each copy module naming the fallback
 * itself — is how one of them comes to render `undefined` instead of a sentence.
 *
 * Falls back per LOCALE, not per key. It suits whole-object copy (a landing page's prose, a date
 * picker's labels): a half-translated page reads worse than an English one. The message CATALOGUE
 * is the opposite case and merges per key — see `catalogWithEnglishFallback`, which is why the two
 * are separate functions rather than one with a flag.
 *
 * Total over every locale the type counts, shipped or not ({@link ALL_SITE_LOCALES}), so the `Record` it returns
 * keeps its promise in a build that does not ship French yet (kitecorp/transferify-ui#3937).
 *
 * @param copy every locale that HAS copy; `en` is required because it is what the others fall back to
 */
export function localeCopyWithEnglishFallback<T>(
	copy: Partial<Record<SiteLocale, T>> & {en: T},
): Record<SiteLocale, T> {
	return Object.fromEntries(
		ALL_SITE_LOCALES.map((locale) => [locale, copy[locale] ?? copy.en]),
	) as Record<SiteLocale, T>;
}
